import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Globe, Loader2, Library, ListChecks, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const SOURCES = [
  { id: 'openalex', label: 'OpenAlex' },
  { id: 'semantic_scholar', label: 'Semantic Scholar' },
  { id: 'crossref', label: 'Crossref' },
  { id: 'arxiv', label: 'arXiv' },
];

export default function LiteratureSearchPanel({ userId, projectId, onIngested, onSendToScreening }) {
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(20);
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [openAccess, setOpenAccess] = useState(false);
  const [sources, setSources] = useState(SOURCES.map((s) => s.id));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({});
  const [ingesting, setIngesting] = useState(false);

  const run = async () => {
    if (query.trim().length < 3) { toast.error('Enter at least 3 characters'); return; }
    setLoading(true);
    try {
      const data = await thesisApi.literatureSearch(userId, {
        project_id: projectId, query: query.trim(), limit: Number(limit) || 20,
        sources, year_from: yearFrom, year_to: yearTo, open_access_only: openAccess,
      });
      setResult(data);
      setSelected({});
      const failed = Object.keys(data.errors || {});
      toast.success(`${data.count} records${failed.length ? ` · ${failed.join(', ')} unavailable` : ''}`);
    } catch (e) {
      toast.error('Search failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const chosen = (result?.records || []).filter((_, i) => selected[i]);

  const addToLibrary = async () => {
    const records = chosen.length ? chosen : result?.records || [];
    setIngesting(true);
    try {
      const res = await thesisApi.literatureIngest(userId, projectId, records);
      toast.success(`Added ${res.ingested} sources · ${res.skipped_duplicates} duplicates`
        + (res.skipped_no_abstract ? ` · ${res.skipped_no_abstract} without abstract skipped` : ''));
      onIngested?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'object' ? d.error : (d || e.message));
    } finally {
      setIngesting(false);
    }
  };

  return (
    <Card data-testid="literature-search-panel">
      <CardHeader>
        <CardTitle className="font-serif flex flex-wrap items-center gap-2" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          <Globe className="w-5 h-5 text-[#1d4ed8]" /> Global literature search
          <Badge variant="outline" className="text-[10px]">OpenAlex · Semantic Scholar · Crossref · arXiv</Badge>
        </CardTitle>
        <CardDescription>
          Search beyond PubMed across 250M+ indexed works. Duplicates are merged by DOI, abstracts
          are pulled in, and anything you keep becomes retrievable evidence for your writing.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Input className="min-w-[260px] flex-1" value={query} placeholder="e.g. step-up approach necrotizing pancreatitis outcomes"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && run()} data-testid="lit-query-input" />
          <Input className="w-20" type="number" value={limit} onChange={(e) => setLimit(e.target.value)}
            data-testid="lit-limit-input" />
          <Input className="w-24" value={yearFrom} placeholder="From yr"
            onChange={(e) => setYearFrom(e.target.value)} data-testid="lit-year-from" />
          <Input className="w-24" value={yearTo} placeholder="To yr"
            onChange={(e) => setYearTo(e.target.value)} data-testid="lit-year-to" />
          <Button onClick={run} disabled={loading} data-testid="lit-search-btn">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Searching…</> : 'Search'}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {SOURCES.map((s) => {
            const on = sources.includes(s.id);
            return (
              <button key={s.id} type="button"
                onClick={() => setSources((p) => (on ? p.filter((x) => x !== s.id) : [...p, s.id]))}
                className={`px-2 py-1 rounded-full border text-[11px] transition-colors ${on
                  ? 'bg-[#1d4ed8] text-white border-[#1d4ed8]'
                  : 'bg-background border-input hover:border-[#1d4ed8]/40'}`}
                data-testid={`lit-source-${s.id}`}>{s.label}</button>
            );
          })}
          <label className="flex items-center gap-1.5 text-xs ml-2">
            <input type="checkbox" checked={openAccess} onChange={(e) => setOpenAccess(e.target.checked)}
              className="accent-teal-700" data-testid="lit-open-access-toggle" />
            Open access only
          </label>
        </div>

        {result && (
          <div className="space-y-2" data-testid="lit-results">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{result.count} records · {chosen.length} selected</span>
              <Button size="sm" variant="outline" onClick={addToLibrary} disabled={ingesting}
                data-testid="lit-add-library-btn">
                {ingesting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Library className="w-3 h-3 mr-1" />}
                Add {chosen.length ? `${chosen.length} selected` : 'all'} to library
              </Button>
              <Button size="sm" variant="outline"
                onClick={() => onSendToScreening?.(chosen.length ? chosen : result.records)}
                data-testid="lit-to-screening-btn">
                <ListChecks className="w-3 h-3 mr-1" /> Send to PRISMA screening
              </Button>
            </div>
            <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
              {result.records.map((r, i) => (
                <label key={`${r.doi || r.title}-${i}`}
                  className="flex gap-2 p-2 rounded-md border border-border hover:border-[#1d4ed8]/40 cursor-pointer"
                  data-testid={`lit-record-${i}`}>
                  <input type="checkbox" checked={!!selected[i]} className="mt-1 accent-teal-700"
                    onChange={(e) => setSelected((p) => ({ ...p, [i]: e.target.checked }))} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium leading-snug">{r.title}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {(r.authors || []).slice(0, 3).join(', ')}{(r.authors || []).length > 3 ? ' et al.' : ''}
                      {r.journal ? ` · ${r.journal}` : ''}{r.year ? ` · ${r.year}` : ''}
                      {typeof r.citation_count === 'number' ? ` · ${r.citation_count} citations` : ''}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      {(r.found_in || [r.source]).map((s) => (
                        <Badge key={s} variant="outline" className="text-[9px]">{s}</Badge>
                      ))}
                      {r.open_access && <Badge className="text-[9px] bg-emerald-600">open access</Badge>}
                      {!r.abstract && <Badge variant="outline" className="text-[9px] border-amber-500 text-amber-700">no abstract</Badge>}
                      {r.doi && <a href={`https://doi.org/${r.doi}`} target="_blank" rel="noreferrer"
                        className="text-[10px] text-[#1d4ed8] inline-flex items-center gap-0.5"
                        onClick={(e) => e.stopPropagation()}>doi <ExternalLink className="w-2.5 h-2.5" /></a>}
                      {r.pdf_url && <a href={r.pdf_url} target="_blank" rel="noreferrer"
                        className="text-[10px] text-[#1d4ed8]" onClick={(e) => e.stopPropagation()}>PDF</a>}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
